import React, { useState, useEffect, useMemo } from "react";
import {
  X,
  RotateCcw,
  Calendar,
  User,
  CreditCard,
  CheckSquare,
  FileText,
  Info,
  Plus,
  Minus,
  Loader2,
  Wallet,
} from "lucide-react";
import { returnService } from "../../api/returnService";
import { accountService } from "../../api/accountService";
import { useNotification } from "../../contexts/NotificationContext";

const fmt = (n) => Number(n || 0).toLocaleString("id-ID");

const formatDateShort = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const formatDateTimeShort = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    const datePart = d.toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    const timePart = d.toLocaleTimeString("id-ID", {
      hour: "2-digit",
      minute: "2-digit",
    }).replace(".", ":");
    return `${datePart}, ${timePart}`;
  } catch {
    return dateStr;
  }
};

const formatDateFull = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const PAYMENT_LABELS = {
  CASH: "Tunai",
  TRANSFER: "Transfer",
  QRIS: "QRIS",
  MIXED: "Campuran",
};

export default function ReturnOrderModal({ isOpen, onClose, order, onSuccess }) {
  const { showSuccess, showError } = useNotification();

  const [loadingInfo, setLoadingInfo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [returnableData, setReturnableData] = useState(null);
  const [returnQtys, setReturnQtys] = useState({});
  const [reason, setReason] = useState("");
  const [refundMethod, setRefundMethod] = useState("CASH");
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  const isAccountSupportedForMethod = (account, method) => {
    if (!account) return false;
    if (account.status && account.status !== "ACTIVE" && account.status !== "Aktif") return false;

    const isLaciKasir = account.name?.toLowerCase().includes("laci kasir");
    if (method === "CASH") {
      return isLaciKasir || account.type === "CASH";
    }

    // Laci Kasir khusus Tunai, tidak boleh muncul di Transfer/QRIS
    if (isLaciKasir) {
      return false;
    }

    // Hanya akun pembayaran yang aktif
    if (account.type && account.type !== "PAYMENT_ACCOUNT" && account.type !== "BANK" && account.type !== "E_WALLET") {
      return false;
    }

    const rawMethods = account.payment_methods || account.description || "";
    if (!rawMethods || String(rawMethods).trim() === "") {
      return true;
    }
    const methods = String(rawMethods)
      .toUpperCase()
      .split(",")
      .map((m) => m.trim());

    if (method === "TRANSFER") {
      return methods.includes("TRANSFER");
    }
    if (method === "QRIS") {
      return methods.includes("QRIS");
    }
    if (method === "MIXED") {
      return methods.includes("CAMPURAN");
    }
    return true;
  };

  // Load payment accounts when modal opens
  useEffect(() => {
    if (!isOpen) return;
    setLoadingAccounts(true);
    accountService
      .list({ page: 1, limit: 100 })
      .then((res) => {
        const rows = res?.data || [];
        const activeAccs = rows.filter((r) => r.status === "ACTIVE" || r.status === "Aktif");
        setAccounts(activeAccs);
        const laci = activeAccs.find(
          (r) => r.name?.toLowerCase().includes("laci kasir") || r.type === "CASH"
        );
        if (laci) {
          setSelectedAccountId(laci.id);
        } else if (activeAccs.length > 0) {
          setSelectedAccountId(activeAccs[0].id);
        }
      })
      .catch((err) => console.error("Gagal memuat akun pembayaran:", err))
      .finally(() => setLoadingAccounts(false));
  }, [isOpen]);

  const availableAccounts = useMemo(() => {
    if (!accounts.length) return [];
    return accounts.filter((acc) => isAccountSupportedForMethod(acc, refundMethod));
  }, [accounts, refundMethod]);

  const handleRefundMethodChange = (newMethod) => {
    setRefundMethod(newMethod);
    if (newMethod === "CASH") {
      const laci = accounts.find((r) => r.name?.toLowerCase().includes("laci kasir") || r.type === "CASH");
      if (laci) setSelectedAccountId(laci.id);
      else if (accounts.length > 0) setSelectedAccountId(accounts[0].id);
    } else {
      const filtered = accounts.filter((acc) => isAccountSupportedForMethod(acc, newMethod));
      if (filtered.length > 0) {
        setSelectedAccountId(filtered[0].id);
      } else {
        setSelectedAccountId("");
      }
    }
  };

  useEffect(() => {
    if (refundMethod === "CASH") {
      const laci = accounts.find((r) => r.name?.toLowerCase().includes("laci kasir") || r.type === "CASH");
      if (laci && selectedAccountId !== laci.id) {
        setSelectedAccountId(laci.id);
      }
    } else {
      const isValid = availableAccounts.some((acc) => acc.id === selectedAccountId);
      if (!isValid) {
        setSelectedAccountId(availableAccounts.length > 0 ? availableAccounts[0].id : "");
      }
    }
  }, [availableAccounts, refundMethod, accounts]);

  // Load returnable items info when modal opens
  useEffect(() => {
    if (!isOpen || !order?.id) return;

    setReturnQtys({});
    setReason("");
    setRefundMethod("CASH");
    setLoadingInfo(true);

    returnService
      .getOrderReturnableInfo(order.id)
      .then((res) => {
        const data = res?.data ?? res;
        setReturnableData(data);
        const initialQtys = {};
        (data?.items || []).forEach((itm) => {
          initialQtys[itm.order_item_id || itm.id] = 0;
        });
        setReturnQtys(initialQtys);
      })
      .catch((err) => {
        console.error("Gagal memuat info retur:", err);
        const rawItems = order.items || order.order_items || [];
        const fallbackItems = rawItems.map((itm) => ({
          order_item_id: itm.id || itm.order_item_id,
          product_id: itm.product_id,
          product_variant_id: itm.product_variant_id,
          item_name: itm.item_name || itm.name || itm.product_name,
          unit_name: itm.unit_name || itm.unit || "PCS",
          unit_price: itm.unit_price || itm.price || 0,
          purchase_price: itm.purchase_price || 0,
          original_quantity: Number(itm.quantity || 0),
          already_returned_qty: 0,
          returnable_quantity: Number(itm.quantity || 0),
        }));

        setReturnableData({
          order_id: order.id,
          transaction_number: order.transaction_number,
          order_date: order.created_at || order.order_date,
          customer_name: order.customer_name || order.customer?.name || "Pelanggan Umum",
          payment_method: order.payment_method || order.payments?.[0]?.payment_method || "Tunai",
          items: fallbackItems,
        });

        const initialQtys = {};
        fallbackItems.forEach((itm) => {
          initialQtys[itm.order_item_id] = 0;
        });
        setReturnQtys(initialQtys);
      })
      .finally(() => {
        setLoadingInfo(false);
      });
  }, [isOpen, order?.id]);

  const items = returnableData?.items || [];

  const handleToggleCheck = (itemId, maxVal) => {
    setReturnQtys((prev) => {
      const cur = Number(prev[itemId] || 0);
      if (cur > 0) {
        return { ...prev, [itemId]: 0 };
      }
      return { ...prev, [itemId]: maxVal > 0 ? 1 : 0 };
    });
  };

  const handleQtyChange = (itemId, val, maxVal) => {
    const num = Math.max(0, Math.min(Number(val || 0), maxVal));
    setReturnQtys((prev) => ({
      ...prev,
      [itemId]: num,
    }));
  };

  const handleIncrement = (itemId, maxVal) => {
    setReturnQtys((prev) => {
      const cur = Number(prev[itemId] || 0);
      if (cur < maxVal) {
        return { ...prev, [itemId]: cur + 1 };
      }
      return prev;
    });
  };

  const handleDecrement = (itemId) => {
    setReturnQtys((prev) => {
      const cur = Number(prev[itemId] || 0);
      if (cur > 0) {
        return { ...prev, [itemId]: cur - 1 };
      }
      return prev;
    });
  };

  // Calculations
  const { totalRefundAmount, totalItemsCount } = useMemo(() => {
    let refund = 0;
    let count = 0;

    items.forEach((itm) => {
      const itemId = itm.order_item_id || itm.id;
      const qty = Number(returnQtys[itemId] || 0);
      if (qty > 0) {
        const originalQty = Number(itm.original_quantity || itm.quantity || 0);
        const effectiveUnitPrice = originalQty > 0
          ? Number(itm.total_price || 0) / originalQty
          : Number(itm.unit_price || 0);
        refund += effectiveUnitPrice * qty;
        count += qty;
      }
    });

    return { totalRefundAmount: refund, totalItemsCount: count };
  }, [items, returnQtys]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (totalItemsCount <= 0) {
      showError("Silakan tentukan minimal 1 jumlah produk yang akan diretur.");
      return;
    }

    const payloadItems = [];
    for (const itm of items) {
      const itemId = itm.order_item_id || itm.id;
      const qty = Number(returnQtys[itemId] || 0);
      if (qty > 0) {
        payloadItems.push({
          order_item_id: itemId,
          product_id: itm.product_id,
          product_variant_id: itm.product_variant_id,
          quantity: qty,
        });
      }
    }

    setSubmitting(true);
    try {
      const res = await returnService.createReturn({
        order_id: order.id,
        refund_method: refundMethod,
        account_id: selectedAccountId || undefined,
        reason: reason.trim() || "Retur Barang",
        return_date: new Date().toISOString(),
        items: payloadItems,
      });

      showSuccess("Retur penjualan berhasil diproses");
      onClose();
      if (onSuccess) {
        onSuccess(res?.data ?? res);
      }
    } catch (err) {
      console.error("Gagal memproses retur:", err);
      showError(err.response?.data?.message || err.message || "Gagal memproses retur barang.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const now = new Date();
  const todayShortStr = formatDateShort(now);
  const todayFullStr = formatDateFull(now);
  const origSaleDateStr = formatDateTimeShort(returnableData?.order_date || order?.created_at || order?.order_date);
  const customerName = returnableData?.customer_name || order?.customer_name || order?.customer?.name || "Pelanggan Umum";
  const transactionNumber = returnableData?.transaction_number || order?.transaction_number || "-";
  const rawPayment = returnableData?.payment_method || order?.payment_method || order?.payments?.[0]?.payment_method || "Tunai";
  const paymentLabel = PAYMENT_LABELS[rawPayment] || rawPayment || "Tunai";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-sm overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 my-auto flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Loading Overlay saat proses submit */}
        {submitting && (
          <div className="absolute inset-0 bg-white/85 backdrop-blur-xs z-50 flex flex-col items-center justify-center animate-in fade-in duration-200">
            <div className="w-14 h-14 rounded-2xl bg-teal-50 flex items-center justify-center text-[#0f766e] mb-3 shadow-xs">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
            <p className="text-sm font-bold text-slate-800">
              Memproses Retur Barang…
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Mohon tunggu sebentar, sedang memperbarui stok dan data retur
            </p>
          </div>
        )}

        {/* === HEADER === */}
        <div className="px-6 py-4 bg-[#0f766e] text-white flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-white flex-shrink-0">
              <RotateCcw className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[11px] font-semibold tracking-wider text-teal-100 uppercase">
                PROSES RETUR
              </p>
              <h2 className="text-xl font-bold tracking-tight text-white leading-tight">
                {transactionNumber}
              </h2>
              <div className="mt-1">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-white text-[#0f766e] shadow-xs">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                  Transaksi Selesai
                </span>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-teal-100 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* === BODY === */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="p-5 sm:p-6 space-y-4 max-h-[74vh] overflow-y-auto bg-slate-50/50">
            {/* 4 Meta Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-white p-3.5 rounded-2xl border border-slate-200/80 shadow-xs">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1 mb-1">
                  <Calendar className="w-3.5 h-3.5 text-slate-400" />
                  TANGGAL PENJUALAN
                </p>
                <p className="text-xs sm:text-sm font-bold text-slate-800 truncate">
                  {origSaleDateStr}
                </p>
              </div>

              <div className="bg-white p-3.5 rounded-2xl border border-slate-200/80 shadow-xs">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1 mb-1">
                  <Calendar className="w-3.5 h-3.5 text-slate-400" />
                  TANGGAL RETUR
                </p>
                <p className="text-xs sm:text-sm font-bold text-slate-800 truncate">
                  {todayShortStr}
                </p>
              </div>

              <div className="bg-white p-3.5 rounded-2xl border border-slate-200/80 shadow-xs">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1 mb-1">
                  <User className="w-3.5 h-3.5 text-slate-400" />
                  PELANGGAN
                </p>
                <p className="text-xs sm:text-sm font-bold text-slate-800 truncate">
                  {customerName}
                </p>
              </div>

              <div className="bg-white p-3.5 rounded-2xl border border-slate-200/80 shadow-xs">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1 mb-1">
                  <CreditCard className="w-3.5 h-3.5 text-slate-400" />
                  METODE BAYAR
                </p>
                <p className="text-xs sm:text-sm font-bold text-slate-800 truncate">
                  {paymentLabel}
                </p>
              </div>
            </div>

            {/* Pilih Item Retur Table */}
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-xs sm:text-sm font-bold text-slate-800 flex items-center gap-2">
                  <CheckSquare className="w-4 h-4 text-[#0f766e]" />
                  Pilih Item Retur
                </h3>
                <span className="text-[11px] text-slate-400">
                  Tentukan jumlah yang akan dikembalikan
                </span>
              </div>

              {loadingInfo ? (
                <div className="py-12 text-center text-sm text-slate-400">
                  Memuat data produk transaksi…
                </div>
              ) : items.length === 0 ? (
                <div className="py-10 text-center text-sm text-slate-400">
                  Tidak ada produk yang dapat diretur.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs sm:text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50/70 text-slate-400 text-[11px] font-bold text-left uppercase tracking-wider">
                        <th className="py-2.5 px-4 text-center w-12">PILIH</th>
                        <th className="py-2.5 px-4">NAMA PRODUK</th>
                        <th className="py-2.5 px-3 text-center">QTY TERJUAL</th>
                        <th className="py-2.5 px-3 text-center">SUDAH DIRETUR</th>
                        <th className="py-2.5 px-4 text-center w-36">QTY RETUR</th>
                        <th className="py-2.5 px-4 text-right">HARGA</th>
                        <th className="py-2.5 px-4 text-right">TOTAL RETUR</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {items.map((itm) => {
                        const itemId = itm.order_item_id || itm.id;
                        const curQty = Number(returnQtys[itemId] || 0);
                        const maxQty = itm.returnable_quantity !== undefined ? itm.returnable_quantity : Number(itm.quantity || itm.original_quantity || 1);
                        const isFullyReturned = maxQty <= 0;
                        const isChecked = curQty > 0;
                        const originalQty = Number(itm.original_quantity || itm.quantity || 0);
                        const effectiveUnitPrice = originalQty > 0
                          ? Number(itm.total_price ?? 0) / originalQty
                          : Number(itm.unit_price || 0);
                        const rowTotalRefund = effectiveUnitPrice * curQty;

                        return (
                          <tr
                            key={itemId}
                            className={`hover:bg-slate-50/70 transition-colors ${
                              isChecked ? "bg-teal-50/30" : ""
                            }`}
                          >
                            <td className="py-3 px-4 text-center">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                disabled={isFullyReturned}
                                onChange={() => handleToggleCheck(itemId, maxQty)}
                                className="w-4 h-4 rounded text-[#0f766e] focus:ring-[#0f766e] accent-[#0f766e] cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                              />
                            </td>
                            <td className="py-3 px-4">
                              <p className="font-semibold text-slate-800">
                                {itm.item_name}
                              </p>
                              {itm.unit_name && (
                                <p className="text-[11px] text-slate-400 font-normal">
                                  Satuan {itm.unit_name}
                                </p>
                              )}
                              {Number(itm.discount_amount || 0) > 0 && (
                                <p className="text-[11px] font-medium text-rose-600">
                                  Diskon Item -Rp {fmt(itm.discount_amount)}
                                </p>
                              )}
                              {Number(itm.additional_cost || 0) > 0 && (
                                <p className="text-[11px] font-medium text-blue-600">
                                  Biaya Tambahan Item +Rp {fmt(itm.additional_cost)}
                                </p>
                              )}
                            </td>
                            <td className="py-3 px-3 text-center font-medium text-slate-700">
                              {itm.original_quantity !== undefined ? itm.original_quantity : itm.quantity}
                            </td>
                            <td className="py-3 px-3 text-center font-medium text-slate-500">
                              {itm.already_returned_qty || 0}
                            </td>
                            <td className="py-3 px-4">
                              {isFullyReturned ? (
                                <span className="inline-block text-[11px] font-semibold text-slate-400 bg-slate-100 px-2.5 py-1 rounded-lg">
                                  Sudah Diretur
                                </span>
                              ) : (
                                <div className="inline-flex items-center justify-center gap-1.5 bg-slate-50 p-1 rounded-xl border border-slate-200">
                                  <button
                                    type="button"
                                    onClick={() => handleDecrement(itemId)}
                                    disabled={curQty <= 0}
                                    className="w-6 h-6 rounded-lg bg-white hover:bg-slate-200 text-slate-700 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed shadow-2xs transition-colors"
                                  >
                                    <Minus className="w-3 h-3" />
                                  </button>
                                  <input
                                    type="number"
                                    min="0"
                                    max={maxQty}
                                    value={curQty}
                                    onChange={(e) => handleQtyChange(itemId, e.target.value, maxQty)}
                                    className="w-8 text-center font-bold text-xs sm:text-sm bg-transparent border-0 focus:outline-none text-slate-800"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleIncrement(itemId, maxQty)}
                                    disabled={curQty >= maxQty}
                                    className="w-6 h-6 rounded-lg bg-teal-50 hover:bg-teal-100 text-[#0f766e] flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed shadow-2xs transition-colors"
                                  >
                                    <Plus className="w-3 h-3" />
                                  </button>
                                </div>
                              )}
                            </td>
                            <td className="py-3 px-4 text-right font-medium text-slate-700">
                              Rp {fmt(effectiveUnitPrice)}
                            </td>
                            <td className="py-3 px-4 text-right font-bold text-slate-900">
                              Rp {fmt(rowTotalRefund)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Lower Section (2 Columns: Alasan & Metode + Ringkasan Retur) */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              {/* Left Column (7 cols): Alasan & Metode */}
              <div className="md:col-span-7 bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs space-y-3.5">
                <div>
                  <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1.5">
                    ALASAN RETUR
                  </label>
                  <textarea
                    rows="2"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Tuliskan alasan retur..."
                    className="w-full px-3.5 py-2 text-xs sm:text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0f766e] bg-white placeholder-slate-400"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1.5">
                    METODE PENGEMBALIAN DANA
                  </label>
                  <select
                    value={refundMethod}
                    onChange={(e) => handleRefundMethodChange(e.target.value)}
                    className="w-full px-3.5 py-2 text-xs sm:text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0f766e] bg-white text-slate-800"
                  >
                    <option value="CASH">Tunai</option>
                    <option value="TRANSFER">Transfer Bank</option>
                    <option value="QRIS">QRIS / E-Wallet</option>
                  </select>
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1.5">
                    AKUN REKENING PENGELUARAN DANA
                  </label>
                  {loadingAccounts ? (
                    <div className="text-xs text-slate-400 py-1.5 flex items-center gap-1.5">
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-[#0f766e]" /> Memuat daftar rekening...
                    </div>
                  ) : refundMethod === "CASH" ? (
                    (() => {
                      const laci = accounts.find((r) => r.name?.toLowerCase().includes("laci kasir") || r.type === "CASH") || availableAccounts[0];
                      return (
                        <div className="w-full px-3.5 py-2.5 rounded-xl border border-teal-200 bg-teal-50/60 flex items-center justify-between text-xs sm:text-sm">
                          <div className="flex items-center gap-2">
                            <Wallet className="w-4 h-4 text-[#0f766e]" />
                            <span className="font-bold text-slate-800">{laci?.name || "Laci Kasir"}</span>
                            <span className="text-[10px] bg-teal-100 text-[#0f766e] font-bold px-2 py-0.5 rounded-full">
                              Auto Laci Kasir
                            </span>
                          </div>
                          <span className="text-xs font-semibold text-slate-600">
                            Saldo: Rp {fmt(laci?.total_balance ?? 0)}
                          </span>
                        </div>
                      );
                    })()
                  ) : availableAccounts.length === 0 ? (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
                      <p className="font-semibold">Tidak ada rekening yang mendukung {refundMethod === "TRANSFER" ? "Transfer Bank" : "QRIS"}.</p>
                      <p className="text-[11px] text-amber-700 mt-0.5">Silakan centang metode ini pada akun di menu <b>Keuangan</b>.</p>
                    </div>
                  ) : (
                    <select
                      value={selectedAccountId}
                      onChange={(e) => setSelectedAccountId(e.target.value)}
                      className="w-full px-3.5 py-2 text-xs sm:text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0f766e] bg-white text-slate-800 font-medium"
                    >
                      {availableAccounts.map((acc) => (
                        <option key={acc.id} value={acc.id}>
                          {acc.name} {acc.account_number ? `(${acc.account_number})` : ""} - Saldo: Rp {fmt(acc.total_balance ?? 0)}
                        </option>
                      ))}
                    </select>
                  )}
                  <p className="text-[10px] text-slate-400 mt-1">
                    {refundMethod === "CASH" 
                      ? "Pengembalian tunai otomatis memotong kas laci kasir pada shift kasir aktif saat ini."
                      : "Dana retur otomatis dicatat sebagai uang keluar dan memotong saldo rekening ini di buku kas keuangan."}
                  </p>
                </div>
              </div>

              {/* Right Column (5 cols): Ringkasan Retur */}
              <div className="md:col-span-5 bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs space-y-2.5">
                <div className="flex items-center gap-1.5 text-slate-700 text-xs font-bold uppercase tracking-wider mb-1">
                  <FileText className="w-3.5 h-3.5 text-slate-500" />
                  <span>RINGKASAN RETUR</span>
                </div>

                <div className="flex items-center justify-between text-xs sm:text-sm">
                  <span className="text-slate-500">Total Pengembalian</span>
                  <span className="font-extrabold text-slate-900">
                    Rp {fmt(totalRefundAmount)}
                  </span>
                </div>

                <div className="flex items-center justify-between text-xs sm:text-sm">
                  <span className="text-slate-500">Stok Kembali</span>
                  <span className="font-extrabold text-emerald-600">
                    +{totalItemsCount} PCS
                  </span>
                </div>

                <div className="flex items-center justify-between text-xs sm:text-sm">
                  <span className="text-slate-500">Dicatat pada</span>
                  <span className="font-semibold text-slate-800">
                    {todayFullStr}
                  </span>
                </div>

                <div className="p-2.5 bg-indigo-50/70 border border-indigo-100 rounded-xl flex items-center gap-2 text-indigo-900 text-[11px] font-medium mt-1">
                  <Info className="w-3.5 h-3.5 text-indigo-600 flex-shrink-0" />
                  <span>Barang retur otomatis kembali ke stok tersedia.</span>
                </div>
              </div>
            </div>
          </div>

          {/* === FOOTER === */}
          <div className="px-6 py-3.5 bg-white border-t border-slate-100 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-xs sm:text-sm font-semibold hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Kembali
            </button>

            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-6 py-2.5 rounded-xl border border-rose-300 bg-white text-rose-500 text-xs sm:text-sm font-semibold hover:bg-rose-50 transition-colors disabled:opacity-50"
              >
                Batal
              </button>

              <button
                type="submit"
                disabled={submitting || totalItemsCount <= 0 || (refundMethod !== "CASH" && availableAccounts.length === 0)}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#0f766e] hover:bg-[#0d665f] text-white text-xs sm:text-sm font-semibold shadow-sm transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RotateCcw className="w-4 h-4" />
                {submitting ? "Memproses Retur…" : "Proses Retur"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
